/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Card, NonIdealState, PanelStack } from '@blueprintjs/core';
import { Fragment, useCallback, useMemo } from 'react';
import { SavedStatesActionCreator } from 'hooks/savedViews/actions';
import { SavedViewTypes } from 'hooks/savedViews/types';
import { useSavedStateSelectors } from 'hooks/savedViews/hooks';
import { RenderWidget } from 'views/savedViews/renderWidget';
import { RenderDashboard } from 'views/savedViews/renderDashboard';
import { SavedExplorer } from 'views/savedViews/savedExplorer';
import {
	useManageViewsState,
	ManageViewsActionCreator,
	ManageViewsStateContext,
} from 'views/savedViews/useManageViewsState';
import { ManageViewsPanel } from 'views/savedViews/sidePanels';

/**
 * Manages selecting and viewing a saved view
 * 
 * @param updateUrlPathCallback function: (savedView) => string // optional function for allowing projects to load different url paths
 * Projects like CyHy have multiple explorers and opening the Saved widget will need to know which url path to navigate to.
 */
export function SavedViews({ updateUrlPathCallback }) {
	const { dispatch: savedStatesDispatch } = useSavedStateSelectors();
	const {
		state: manageViewsState,
		state: { selectedView, isEditing, isCreating, renderComponent },
		dispatch: manageViewsDispatch,
	} = useManageViewsState();

	const manageViewContextValue = useMemo(() => {
		return { state: manageViewsState, dispatch: manageViewsDispatch };
	}, [manageViewsState, manageViewsDispatch]);

	const toggleFavorite = useCallback(() => {
		const updatedView = {
			...selectedView,
			metaData: {
				...selectedView.metaData,
				isFavorite: !selectedView.metaData.isFavorite,
			},
		};
		
		savedStatesDispatch(SavedStatesActionCreator.updateView(updatedView));
		manageViewsDispatch(ManageViewsActionCreator.setSelectedView(updatedView));
	}, [selectedView, savedStatesDispatch, manageViewsDispatch]);

	return (
		<ManageViewsStateContext.Provider value={manageViewContextValue}>
			<Fragment>
				<div css={container}>
					<Card css={panelWrapper}>
						<PanelStack
							initialPanel={{
								component: ManageViewsPanel,
								props: {
									selectedView,
									isEditing,
									isCreating,
									manageViewsDispatch: manageViewsDispatch,
								},
								title: 'Manage Views',
							}}
							renderActivePanelOnly={true}
							showPanelHeader={false}
						/>
					</Card>
					<Card cy-test="widget-card" css={viewWrapper}>
						{selectedView?.metaData?.id || isCreating || isEditing ? (
							renderComponent === SavedViewTypes.Explorer ? (
								<SavedExplorer toggleFavorite={toggleFavorite} key={selectedView?.metaData.id} updateUrlPathCallback={updateUrlPathCallback} />
							) : renderComponent === SavedViewTypes.Widget ? (
								<RenderWidget widget={selectedView} key={selectedView?.metaData.id} toggleFavorite={toggleFavorite} updateUrlPathCallback={updateUrlPathCallback} />
							) : renderComponent === SavedViewTypes.Dashboard ? (
								<RenderDashboard toggleFavorite={toggleFavorite} key={selectedView?.metaData.id} />
							) : null
						) : (
							<NonIdealState icon={'clean'} title={'Select a view'} />
						)}
					</Card>
				</div>
			</Fragment>
		</ManageViewsStateContext.Provider>
	);
}

const container = css`
	flex: 1 1 auto;
	overflow: hidden;
	display: grid;
	grid-template-columns: 300px 1fr;
	grid-template-rows: 1fr;
	grid-gap: 6px;
	padding: 6px;
`;

const panelWrapper = css`
	padding: 0;
	.bp3-panel-stack {
		height: 100%;
		border-radius: 3px;
	}
`;

const viewWrapper = css`
	overflow: hidden;
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: stretch;
	padding: 10px;
`;
